import { useMemo, useState } from "react";
import { Copy, Layers, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { brl } from "@/lib/api";

const clone = (g) => ({ ...g, max: g.max ?? (g.type === "single" ? 1 : ""), options: (g.options || []).map((o) => ({ name: o.name, price: o.price })) });
const optsText = (g) => (g.options || []).map((o) => (Number(o.price) ? `${o.name} +${brl(o.price)}` : o.name)).join(", ");
const named = (gs) => (gs || []).filter((g) => g.name?.trim());

function uniqueNames(groups, existing) {
  const taken = new Set(existing.map((g) => g.name.trim().toLowerCase()));
  return groups.map((g) => {
    const base = g.name.trim();
    let name = base;
    for (let n = 1; taken.has(name.toLowerCase()); n += 1) name = `${base} (cópia${n > 1 ? ` ${n}` : ""})`;
    taken.add(name.toLowerCase());
    return { ...g, name };
  });
}

function categoryVersions(products, f) {
  const cat = f.category_id === "geral" ? null : f.category_id;
  const map = new Map();
  products.filter((p) => p.id !== f.id && (p.category_id || null) === cat).forEach((p) => named(p.option_groups).forEach((g) => {
    const key = `${g.name.trim().toLowerCase()}|${JSON.stringify((g.options || []).map((o) => [o.name.trim().toLowerCase(), Number(o.price) || 0]))}`;
    const v = map.get(key) || { key, group: g, products: [] };
    v.products.push(p.name);
    map.set(key, v);
  }));
  return [...map.values()].sort((a, b) => b.products.length - a.products.length || a.group.name.localeCompare(b.group.name));
}

function GroupLine({ g, extra }) {
  return (
    <span className="min-w-0 flex-1">
      <span className="font-medium">{g.name}</span>{extra}
      <span className="block truncate text-xs text-muted-foreground">{optsText(g)}</span>
    </span>
  );
}

function ProductPicker({ products, f, onPick }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const s = q.trim().toLowerCase();
  const list = products.filter((p) => p.id !== f.id && named(p.option_groups).length && p.name.toLowerCase().includes(s)).slice(0, 40);
  return (
    <>
      <DialogHeader><DialogTitle>Copiar de outro produto</DialogTitle><DialogDescription>Digite o nome, escolha o produto e copie os grupos com nomes e valores.</DialogDescription></DialogHeader>
      <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome do produto" className="pl-9" data-testid="copy-groups-search" /></div>
      <ul className="max-h-56 divide-y overflow-y-auto rounded-lg border" data-testid="copy-groups-product-list">
        {list.map((p) => (
          <li key={p.id}><button type="button" onClick={() => setSel(p)} className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${sel?.id === p.id ? "bg-accent" : ""}`} data-testid={`copy-groups-product-${p.id}`}>
            <span className="font-medium">{p.name}</span><span className="block truncate text-xs text-muted-foreground">{named(p.option_groups).map((g) => g.name).join(" · ")}</span>
          </button></li>
        ))}
        {!list.length && <li className="p-4 text-sm text-muted-foreground" data-testid="copy-groups-empty">Nenhum produto com complementos encontrado</li>}
      </ul>
      {sel && <div className="space-y-1 rounded-lg border p-3 text-sm" data-testid="copy-groups-preview">{named(sel.option_groups).map((g, i) => <div key={i} className="flex"><GroupLine g={g} /></div>)}</div>}
      <DialogFooter><Button disabled={!sel} onClick={() => onPick(named(sel.option_groups))} data-testid="copy-groups-next-btn">Copiar {sel ? `${named(sel.option_groups).length} grupo(s)` : ""}</Button></DialogFooter>
    </>
  );
}

function CategoryPicker({ products, f, catName, onPick }) {
  const versions = useMemo(() => categoryVersions(products, f), [products, f]);
  const [sel, setSel] = useState(() => new Set());
  const toggle = (k) => setSel((x) => { const n = new Set(x); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const empty = !f.category_id ? "Escolha a categoria na aba Geral primeiro" : !versions.length ? "Nenhum outro produto desta categoria tem complementos" : "";
  return (
    <>
      <DialogHeader><DialogTitle>Copiar da categoria {catName}</DialogTitle><DialogDescription>Grupos usados nos produtos da categoria, do mais usado para o menos usado. Marque os que quer importar.</DialogDescription></DialogHeader>
      {empty ? <p className="rounded-lg border p-4 text-sm text-muted-foreground" data-testid="copy-groups-empty">{empty}</p> : (
        <ul className="max-h-72 divide-y overflow-y-auto rounded-lg border" data-testid="copy-groups-version-list">
          {versions.map((v, i) => (
            <li key={v.key}><label className="flex cursor-pointer items-start gap-3 px-3 py-2 text-sm transition-colors hover:bg-accent">
              <Checkbox checked={sel.has(v.key)} onCheckedChange={() => toggle(v.key)} className="mt-0.5" data-testid={`copy-groups-version-${i}`} />
              <GroupLine g={v.group} extra={<span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] text-primary">{v.products.length} produto(s)</span>} />
              <span className="hidden max-w-[40%] truncate text-xs text-muted-foreground sm:block">{v.products.join(", ")}</span>
            </label></li>
          ))}
        </ul>
      )}
      <DialogFooter><Button disabled={!sel.size} onClick={() => onPick(versions.filter((v) => sel.has(v.key)).map((v) => v.group))} data-testid="copy-groups-next-btn">Copiar {sel.size || ""} grupo(s)</Button></DialogFooter>
    </>
  );
}

function ConfirmMode({ count, onChoose, onBack }) {
  return (
    <>
      <DialogHeader><DialogTitle>Copiar {count} grupo(s)</DialogTitle><DialogDescription>Deseja substituir os complementos atuais ou adicionar aos existentes?</DialogDescription></DialogHeader>
      <p className="text-xs text-muted-foreground">Ao adicionar, um grupo com nome igual a um existente entra com "(cópia)" no nome.</p>
      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={onBack} data-testid="copy-groups-back-btn">Voltar</Button>
        <Button variant="outline" onClick={() => onChoose("replace")} data-testid="copy-groups-replace-btn">Substituir</Button>
        <Button onClick={() => onChoose("add")} data-testid="copy-groups-add-btn">Adicionar aos existentes</Button>
      </DialogFooter>
    </>
  );
}

export function CopyGroups({ f, products, categories, onChange }) {
  const [mode, setMode] = useState(null);
  const [pending, setPending] = useState(null);
  const current = named(f.option_groups);
  const catName = f.category_id === "geral" ? "Geral" : categories.find((c) => c.id === f.category_id)?.name || "";
  const close = () => { setMode(null); setPending(null); };
  const apply = (groups, how) => {
    const add = how === "add";
    onChange([...(add ? f.option_groups || [] : []), ...uniqueNames(groups.map(clone), add ? current : [])]);
    close();
    toast.success(`${groups.length} grupo(s) copiado(s). Revise e clique em Salvar produto`);
  };
  const pick = (groups) => (current.length ? setPending(groups) : apply(groups, "replace"));
  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setMode("product")} data-testid="copy-groups-from-product-btn"><Copy className="mr-1 h-4 w-4" />Copiar de outro produto</Button>
      <Button type="button" size="sm" variant="outline" onClick={() => setMode("category")} data-testid="copy-groups-from-category-btn"><Layers className="mr-1 h-4 w-4" />Copiar da categoria</Button>
      <Dialog open={!!mode} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg" data-testid="copy-groups-dialog">
          {pending ? <ConfirmMode count={pending.length} onChoose={(how) => apply(pending, how)} onBack={() => setPending(null)} />
            : mode === "product" ? <ProductPicker products={products} f={f} onPick={pick} />
              : <CategoryPicker products={products} f={f} catName={catName} onPick={pick} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
